import _ from 'underscore';
import URI from 'urijs';

type UriInstance = ReturnType<typeof URI>;

interface UriProperties {
  uri: UriInstance;
  isRelative: boolean;
  segments: string[];
  segmentsLowerCase: string[];
}

interface PathFormatEntry extends Partial<UriProperties> {
  value: unknown;
  isValid: boolean;
}

interface PathAndBasenameFormat {
  pathname: PathFormatEntry;
  basename: PathFormatEntry;
  isPathStartWithbasename?: boolean;
}

const pathAndBasenameFormatted = (pathname: unknown, basename: unknown): PathAndBasenameFormat => {
  const format: PathAndBasenameFormat = {
    pathname: {
      value: pathname,
      isValid: _.isString(pathname),
    },
    basename: {
      value: basename,
      isValid: _.isString(basename),
    },
  };

  if (format.pathname.isValid && format.basename.isValid) {
    const getUriProperties = (path: string): UriProperties => {
      const uri = URI(path);
      const isRelative = uri.is('relative');
      const segments = uri.segment();
      const segmentsLowerCase = _.map(segments, (s: string) => s.toLowerCase());
      return {
        uri,
        isRelative,
        segments,
        segmentsLowerCase,
      };
    };

    _.extend(format.pathname, getUriProperties(pathname as string));
    _.extend(format.basename, getUriProperties(basename as string));
    const pathnameFormat = format.pathname as PathFormatEntry & UriProperties;
    const basenameFormat = format.basename as PathFormatEntry & UriProperties;
    format.isPathStartWithbasename = pathnameFormat.isRelative && _.isEqual(
      pathnameFormat.segmentsLowerCase.slice(0, basenameFormat.segmentsLowerCase.length),
      basenameFormat.segmentsLowerCase
    );
  }

  return format;
};

/**
 * Remove the basename from the relative path
 * @param {String} pathname The string value of a URL's path
 * @param {String} basename The string value of the site basename (between hostname and spa routing starting point)
 * @returns {String} The SPA path without basename if exists, the pathname otherwise. In case pathname is not a string, returns null
 */
export const removeBasename = (pathname: unknown, basename?: unknown): string | null => {
  const format = pathAndBasenameFormatted(pathname, basename);
  if (!format.pathname.isValid) {
    return null;
  }
  if (!format.basename.isValid) {
    return pathname as string;
  }
  if (format.isPathStartWithbasename) {
    const pathnameFormat = format.pathname as PathFormatEntry & UriProperties;
    const basenameFormat = format.basename as PathFormatEntry & UriProperties;
    const segmentWithoutBasename = pathnameFormat.segments.slice(basenameFormat.segments.length);
    return pathnameFormat.uri.segment(segmentWithoutBasename).toString();
  }
  return pathname as string;
};

/**
 * Add the basename from the path (if relative)
 * @param {String} pathname The string value of a URL's path
 * @param {String} basename The string value of the site basename (between hostname and spa routing starting point)
 * @returns {String} The full relative path with basename if relative, the same pathname otherwise. In case pathname is not a string, returns null
 */
export const addBasename = (pathname: unknown, basename?: unknown): string | null => {
  const format = pathAndBasenameFormatted(pathname, basename);
  if (!format.pathname.isValid) {
    return null;
  }
  if (!format.basename.isValid) {
    return pathname as string;
  }
  const pathnameFormat = format.pathname as PathFormatEntry & UriProperties;
  const basenameFormat = format.basename as PathFormatEntry & UriProperties;
  if (pathnameFormat.isRelative && !format.isPathStartWithbasename) {
    return pathnameFormat.uri.segment([...basenameFormat.segments, ...pathnameFormat.segments]).toString();
  }
  return pathname as string;
};

export type { PathAndBasenameFormat, PathFormatEntry, UriProperties };
